import { ActionIcon, Alert, Button, Code, CopyButton, Group, Paper, Stack, Table, Text, TextInput, Title, Tooltip } from '@mantine/core';
import { IconCopy, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiKeys, useCreateApiKey, useDeleteApiKey } from '@/api/account';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { useApiError } from '@/hooks/useApiError';
import { formatDate } from '@/lib/format';

/** Persönliche API-Schlüssel (API-01). */
export function ApiKeysSection() {
  const { t } = useTranslation();
  const { prefs } = useCurrentWorkspace();
  const onError = useApiError();
  const { data: keys = [] } = useApiKeys();
  const create = useCreateApiKey();
  const remove = useDeleteApiKey();
  const [name, setName] = useState('');
  const [token, setToken] = useState<string | null>(null);

  return (
    <Paper withBorder p="md">
      <Title order={5} mb="xs">
        {t('apiKeys.title')}
      </Title>
      <Text size="sm" c="dimmed" mb="sm">
        {t('apiKeys.text')}
      </Text>
      <Stack>
        {token && (
          <Alert color="green" title={t('apiKeys.created')}>
            <Group gap="xs" wrap="nowrap">
              <Code style={{ wordBreak: 'break-all' }}>{token}</Code>
              <CopyButton value={token}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? t('common.copied') : t('apiKeys.copy')}>
                    <ActionIcon variant="subtle" onClick={copy} aria-label={t('apiKeys.copy')}>
                      <IconCopy size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
            <Text size="xs" mt="xs">
              {t('apiKeys.onlyOnce')}
            </Text>
          </Alert>
        )}
        <Group align="flex-end">
          <TextInput label={t('apiKeys.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} w={260} />
          <Button
            disabled={!name.trim()}
            loading={create.isPending}
            onClick={() =>
              create.mutate(
                { name: name.trim() },
                {
                  onSuccess: (r) => {
                    setToken(r.token);
                    setName('');
                  },
                  onError,
                },
              )
            }
          >
            {t('apiKeys.generate')}
          </Button>
        </Group>
        {keys.length > 0 && (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('apiKeys.name')}</Table.Th>
                <Table.Th>{t('apiKeys.prefix')}</Table.Th>
                <Table.Th>{t('apiKeys.createdAt')}</Table.Th>
                <Table.Th>{t('apiKeys.lastUsed')}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {keys.map((k) => (
                <Table.Tr key={k.id}>
                  <Table.Td>{k.name}</Table.Td>
                  <Table.Td>
                    <Code>ft_{k.prefix}_…</Code>
                  </Table.Td>
                  <Table.Td>{formatDate(k.createdAt, prefs)}</Table.Td>
                  <Table.Td>{k.lastUsedAt ? formatDate(k.lastUsedAt, prefs) : '–'}</Table.Td>
                  <Table.Td>
                    <ActionIcon variant="subtle" color="red" aria-label={t('common.delete')} onClick={() => remove.mutate(k.id, { onError })}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Paper>
  );
}
